import React, { FC, memo, useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import Image from 'next/image'

import LoaderGrey from '../../assets/gifs/loader-grey.gif'
import akamaiImageLoader, { isAkamaiOptimizable } from '../../utils/akamai-image-loader'

interface Props {
    src: string
    alt: string
    objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
    priority?: boolean
    quality?: number | string
    showPlaceholder?: boolean
    containerComponent?: React.ElementType
    width?: number
    height?: number
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void
    imwidth?: number
    sizes?: string
}

const NextImage: FC<Props> = ({
    src,
    alt,
    objectFit,
    priority = false,
    quality = 75,
    showPlaceholder = false,
    containerComponent,
    width,
    height,
    onLoad,
    imwidth,
    sizes,
}) => {
    const [hasPlaceholder, setHasPlaceholder] = useState(true)

    const ImageContainer = containerComponent || Container

    const handleImageLoad = useCallback(
        (e: React.SyntheticEvent<HTMLImageElement>) => {
            if (showPlaceholder) {
                setHasPlaceholder(false)
            }
            onLoad?.(e)
        },
        [showPlaceholder, onLoad],
    )

    // Absolute images on an Akamai IVM host are optimized at the edge, so skip the on-pod
    // Next optimizer via a custom loader; everything else keeps the default behaviour.
    const useAkamaiLoader = isAkamaiOptimizable(src)
    const imageUrl = !useAkamaiLoader && src && imwidth ? `${src}?imwidth=${imwidth}` : src
    const useFill = !width || !height

    return (
        <ImageContainer>
            {showPlaceholder && hasPlaceholder && (
                <Image src={LoaderGrey} alt="placeholder" fill style={{ objectFit: 'contain' }} />
            )}

            <Image
                src={imageUrl}
                loader={useAkamaiLoader ? akamaiImageLoader : undefined}
                alt={alt}
                {...(useFill ? { fill: true } : { width, height })}
                style={{ objectFit: objectFit ?? 'cover' }}
                onLoad={handleImageLoad}
                priority={priority}
                quality={quality as number | `${number}` | undefined}
                sizes={sizes}
            />
        </ImageContainer>
    )
}

const Container = styled.div(
    ({ theme }) => css`
        position: relative;
        overflow: hidden;
        height: ${theme.layout.fill};
        width: ${theme.layout.fill};
    `,
)

export default memo(NextImage)
